import { getAPI } from "@/lib/api";
import {
  getAll,
  getDeals,
  getDealstageAvgTimesByCompanyId,
} from "@/lib/supabase";
import { useAppStore } from "@/stores";
import { getDealOwner, getUserIdsForTeam } from "@revelate/utils";
import { NestedCompany, NestedCurrency, NestedTeam } from "@revelate/types";
import {
  addMonths,
  endOfMonth,
  endOfYear,
  isAfter,
  isBefore,
  startOfYear,
  startOfQuarter,
  subQuarters,
  subYears,
} from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { useLocation } from "wouter";
import {
  getRevenueIntelligencePeriod,
  persistRevenueIntelligencePeriod,
  REVENUE_INTELLIGENCE_FILTER_CONTEXT_CHANGED_EVENT,
} from "../lib/revenue-intelligence-routes";
import {
  applyExpectedCloseV2Forecast,
  applyExpectedCloseV2DealContributions,
} from "../expected-close-v2/localExpectedCloseForecast";
import { getExpectedCloseV2CompanyForecast } from "../expected-close-v2/expectedCloseV2Data";
import { getCompanyTarget } from "../lib/companies";
import {
  evaluateRiskForecast,
  evaluateOpenDealsForForecast,
  prepareDealsForRiskAssessment,
} from "../lib/risk";
import { classifyStateOfSalesForecastSources } from "./stateOfSalesLoading";
import { includeV2ForecastOpenDeals } from "./prepareForecastOpenDeals";

type PreparedForecastDeal = ReturnType<typeof prepareDealsForRiskAssessment>[number];

export type ForecastTimePeriod =
  | "this_month"
  | "next_month"
  | "prev_quarter"
  | "this_quarter"
  | "next_quarter"
  | "next_6_months"
  | "this_year";

export type StateOfSalesV2FilterState = {
  accumulated: boolean;
  owners: string[];
  period: ForecastTimePeriod;
  pipelines: string[];
  teams: string[];
};

const FORECAST_PERIODS: ForecastTimePeriod[] = [
  "this_month",
  "next_month",
  "prev_quarter",
  "this_quarter",
  "next_quarter",
  "next_6_months",
  "this_year",
];

const now = new Date();
const startOfPreviousForecastQuarter = startOfQuarter(subQuarters(now, 2));
const startOfCurrentForecastYear = startOfYear(now);
const startOfForecastFetchRange = isBefore(
  startOfCurrentForecastYear,
  startOfPreviousForecastQuarter,
)
  ? startOfCurrentForecastYear
  : startOfPreviousForecastQuarter;
const endOfForecastFetchRange = isAfter(endOfYear(now), endOfMonth(addMonths(now, 5)))
  ? endOfYear(now)
  : endOfMonth(addMonths(now, 5));

const performanceNow = () => typeof performance === "undefined"
  ? Date.now()
  : performance.now();

function getInitialParam(name: string, fallback: string) {
  if (typeof window === "undefined") return fallback;
  return new URLSearchParams(window.location.search).get(name) ?? fallback;
}

function getInitialMultiParam(name: string) {
  const value = getInitialParam(name, "all");
  if (!value || value === "all") return [];
  return value.split(",").filter(Boolean);
}

function serializeMultiParam(values: string[]) {
  return values.length ? values.join(",") : "all";
}

function isForecastTimePeriod(value: string): value is ForecastTimePeriod {
  return FORECAST_PERIODS.includes(value as ForecastTimePeriod);
}

export function buildStateOfSalesV2Query(filters: StateOfSalesV2FilterState) {
  return new URLSearchParams({
    period: filters.period,
    pipeline: serializeMultiParam(filters.pipelines),
    team: serializeMultiParam(filters.teams),
    owner: serializeMultiParam(filters.owners),
    accumulated: String(filters.accumulated),
  }).toString();
}

export function buildStateOfSalesV2Href(
  view: "overview" | "forecast",
  filters: StateOfSalesV2FilterState,
) {
  const path = view === "forecast"
    ? "/revenue-intelligence/state-of-sales-v2/forecast"
    : "/state-of-sales";
  return `${path}?${buildStateOfSalesV2Query(filters)}`;
}

export function filterDealsByGlobalSelection(
  deals: PreparedForecastDeal[],
  teams: NestedTeam[],
  filters: Pick<StateOfSalesV2FilterState, "owners" | "pipelines" | "teams">,
) {
  const selectedTeamOwnerIds = new Set(
    filters.teams.flatMap((teamId) => getUserIdsForTeam(
      teams,
      Number(teamId),
      true,
    )),
  );

  return deals.filter((deal) => {
    if (filters.pipelines.length && !filters.pipelines.includes(deal.pipeline)) {
      return false;
    }

    const dealOwnerId = getDealOwner(deal)?.id;
    if (filters.teams.length && (!dealOwnerId || !selectedTeamOwnerIds.has(dealOwnerId))) {
      return false;
    }
    if (filters.owners.length && (!dealOwnerId || !filters.owners.includes(dealOwnerId))) {
      return false;
    }
    return true;
  });
}

export type UseStateOfSalesV2ForecastOptions = {
  enabled?: boolean;
  initialFilters?: Partial<StateOfSalesV2FilterState>;
  controlledFilters?: StateOfSalesV2FilterState;
  syncUrl?: boolean;
  view?: "overview" | "forecast" | "pipeline-risk" | "pipeline-health";
};

export function useStateOfSalesV2Forecast(
  options: UseStateOfSalesV2ForecastOptions = {},
) {
  const { currentUser, companyCurrency } = useAppStore((state) => state);
  const [location, setLocation] = useLocation();
  const enabled = options.enabled ?? true;
  const syncUrl = options.syncUrl ?? true;
  const initialPeriod = options.initialFilters?.period
    ?? (syncUrl && typeof window !== "undefined"
      ? getRevenueIntelligencePeriod(window.location.href)
      : "next_6_months");
  const [period, setPeriod] = useState<ForecastTimePeriod>(
    isForecastTimePeriod(initialPeriod) ? initialPeriod : "next_6_months",
  );
  const [selectedPipelines, setSelectedPipelines] = useState<string[]>(
    () => options.initialFilters?.pipelines
      ?? (syncUrl ? getInitialMultiParam("pipeline") : []),
  );
  const [selectedTeams, setSelectedTeams] = useState<string[]>(
    () => options.initialFilters?.teams
      ?? (syncUrl ? getInitialMultiParam("team") : []),
  );
  const [selectedOwners, setSelectedOwners] = useState<string[]>(
    () => options.initialFilters?.owners
      ?? (syncUrl ? getInitialMultiParam("owner") : []),
  );
  const [accumulated, setAccumulated] = useState(
    () => options.initialFilters?.accumulated
      ?? (syncUrl && getInitialParam("accumulated", "false") === "true"),
  );
  const stateOfSalesV2Path = location.split("?")[0];
  const resolvedView = options.view ?? (
    stateOfSalesV2Path.endsWith("/forecast")
      ? "forecast"
      : stateOfSalesV2Path.endsWith("/pipeline-risk")
        ? "pipeline-risk"
        : stateOfSalesV2Path.endsWith("/pipeline-health")
          ? "pipeline-health"
          : "overview"
  );

  const filters = useMemo<StateOfSalesV2FilterState>(() => options.controlledFilters ?? ({
    accumulated,
    owners: selectedOwners,
    period,
    pipelines: selectedPipelines,
    teams: selectedTeams,
  }), [options.controlledFilters, accumulated, period, selectedOwners, selectedPipelines, selectedTeams]);

  useEffect(() => {
    if (!syncUrl) return;
    const params = new URLSearchParams(window.location.search);
    params.set("period", period);
    params.set("pipeline", serializeMultiParam(selectedPipelines));
    params.set("team", serializeMultiParam(selectedTeams));
    params.set("owner", serializeMultiParam(selectedOwners));
    params.set("accumulated", String(accumulated));
    persistRevenueIntelligencePeriod(period);
    setLocation(`${window.location.pathname}?${params.toString()}`, { replace: true });
    window.dispatchEvent(new Event(REVENUE_INTELLIGENCE_FILTER_CONTEXT_CHANGED_EVENT));
  }, [accumulated, period, selectedOwners, selectedPipelines, selectedTeams, setLocation, syncUrl]);

  const companyId = currentUser?.company_id?.toString() ?? null;
  const usesExpectedCloseV2 = true;
  const swrOptions = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };

  const expectedCloseV2Query = useSWR(
    enabled && companyId
      ? ["query:expectedCloseV2Forecast", companyId]
      : null,
    ([, requestedCompanyId]) => getExpectedCloseV2CompanyForecast(Number(requestedCompanyId)),
    swrOptions,
  );

  const dealsQuery = useSWR(
    enabled && companyId ? [
      "query:dealsForForecast",
      companyId,
      startOfForecastFetchRange.toISOString(),
      endOfForecastFetchRange.toISOString(),
    ] : null,
    ([, id, from, to]) => getDeals(id, {
      dateRange: { from, to },
      nonZeroValue: true,
    }),
    swrOptions,
  );

  const priorYearWonQuery = useSWR(
    enabled && companyId ? [
      "query:stateOfSalesV2PriorYearWonDeals",
      companyId,
      subYears(startOfForecastFetchRange, 1).toISOString(),
      subYears(endOfForecastFetchRange, 1).toISOString(),
    ] : null,
    ([, id, from, to]) => getDeals(id, {
      dateRange: { from, to },
      onlyWon: true,
      nonZeroValue: true,
    }),
    swrOptions,
  );

  const currenciesQuery = useSWR(
    enabled && companyId ? ["query:currencies", companyId] : null,
    ([, id]) => getAll("currencies", id),
    swrOptions,
  );

  const teamsQuery = useSWR(
    enabled && companyId ? ["query:teams", companyId] : null,
    ([, id]) => getAll("teams", id),
    swrOptions,
  );

  const pipelinesQuery = useSWR(
    enabled && companyId ? ["query:pipelines", companyId] : null,
    ([, id]) => getAPI(`providers/hubspot/pipelines/${id}`),
    swrOptions,
  );

  const stageTimesQuery = useSWR(
    enabled && companyId ? ["query:dealstageAvgTimes", companyId] : null,
    ([, id]) => id ? getDealstageAvgTimesByCompanyId(Number(id)) : [],
    swrOptions,
  );

  // All-open enrichment is intentionally outside the first Forecast request wave.
  // The date-bounded Deal population can render the Forecast immediately; this
  // broader population fills in Deal evidence and any V2 Deals outside that range.
  const canLoadMovementOpenDeals = Boolean(
    enabled
    && companyId
    && dealsQuery.data !== undefined
    && currenciesQuery.data !== undefined
    && pipelinesQuery.data !== undefined,
  );
  const movementOpenDealsQuery = useSWR(
    canLoadMovementOpenDeals ? ["query:stateOfSalesAllOpenDealsBase", companyId] : null,
    ([, id]) => getDeals(id, {
      onlyOpen: true,
      nonZeroValue: true,
    }),
    swrOptions,
  );

  const sourceState = classifyStateOfSalesForecastSources({
    requireMovement: false,
    requireTeams: selectedTeams.length > 0,
    sources: {
      currencies: currenciesQuery,
      deals: dealsQuery,
      movement: movementOpenDealsQuery,
      pipelines: pipelinesQuery,
      prior_year_actual: priorYearWonQuery,
      stage_times: stageTimesQuery,
      teams: teamsQuery,
    },
  });
  const isLoading = Boolean(
    enabled && (
      !currentUser?.company
      || sourceState.requiredLoading
      || (usesExpectedCloseV2 && expectedCloseV2Query.isLoading)
    ),
  );
  const error = sourceState.requiredError
    ?? (usesExpectedCloseV2 ? expectedCloseV2Query.error : null);
  const requiredErrorSourceKey = sourceState.requiredErrorSources.join(",");
  const optionalErrorSourceKey = sourceState.optionalErrorSources.join(",");

  useEffect(() => {
    if (requiredErrorSourceKey) {
      console.warn(
        `[State of Sales] Required ${resolvedView} sources unavailable: ${requiredErrorSourceKey}`,
      );
    }
    if (optionalErrorSourceKey) {
      console.warn(
        `[State of Sales] Optional ${resolvedView} sources unavailable: ${optionalErrorSourceKey}`,
      );
    }
  }, [optionalErrorSourceKey, requiredErrorSourceKey, resolvedView]);

  const preparedDeals = useMemo(() => {
    if (!currentUser?.company || !currenciesQuery.data) return [];
    return prepareDealsForRiskAssessment(
      dealsQuery.data ?? [],
      pipelinesQuery.data,
      currenciesQuery.data as NestedCurrency[],
      currentUser.company as unknown as NestedCompany,
      stageTimesQuery.data,
    );
  }, [
    currenciesQuery.data,
    currentUser?.company,
    dealsQuery.data,
    pipelinesQuery.data,
    stageTimesQuery.data,
  ]);

  const preparedPriorYearWonDeals = useMemo(() => {
    if (!currentUser?.company || !currenciesQuery.data) return [];
    return prepareDealsForRiskAssessment(
      priorYearWonQuery.data ?? [],
      pipelinesQuery.data,
      currenciesQuery.data as NestedCurrency[],
      currentUser.company as unknown as NestedCompany,
      stageTimesQuery.data,
    );
  }, [
    currenciesQuery.data,
    currentUser?.company,
    pipelinesQuery.data,
    priorYearWonQuery.data,
    stageTimesQuery.data,
  ]);

  const preparedMovementOpenDeals = useMemo(() => {
    if (!currentUser?.company || !currenciesQuery.data) return [];
    return prepareDealsForRiskAssessment(
      movementOpenDealsQuery.data ?? [],
      pipelinesQuery.data,
      currenciesQuery.data as NestedCurrency[],
      currentUser.company as unknown as NestedCompany,
      stageTimesQuery.data,
    );
  }, [
    currenciesQuery.data,
    currentUser?.company,
    movementOpenDealsQuery.data,
    pipelinesQuery.data,
    stageTimesQuery.data,
  ]);

  const teams = useMemo(
    () => (teamsQuery.data ?? []) as NestedTeam[],
    [teamsQuery.data],
  );
  const filteredPreparedDeals = useMemo(
    () => filterDealsByGlobalSelection(preparedDeals, teams, filters),
    [filters, preparedDeals, teams],
  );
  const filteredPreparedMovementOpenDeals = useMemo(
    () => filterDealsByGlobalSelection(preparedMovementOpenDeals, teams, filters),
    [filters, preparedMovementOpenDeals, teams],
  );
  const preparedForecastOpenDeals = useMemo(
    () => movementOpenDealsQuery.data === undefined
      ? preparedDeals.filter(deal => !deal.is_closed)
      : preparedMovementOpenDeals,
    [movementOpenDealsQuery.data, preparedDeals, preparedMovementOpenDeals],
  );
  const expectedCloseV2CompanyForecast = expectedCloseV2Query.data;
  const preparedV2ForecastOpenDeals = useMemo(() => {
    if (!currentUser?.company || !currenciesQuery.data) return preparedForecastOpenDeals;
    return includeV2ForecastOpenDeals({
      legacyOpenDeals: preparedForecastOpenDeals,
      rawOpenDeals: movementOpenDealsQuery.data ?? dealsQuery.data,
      companyForecast: expectedCloseV2CompanyForecast,
      pipelines: pipelinesQuery.data,
      currencies: currenciesQuery.data as NestedCurrency[],
      company: currentUser.company as unknown as NestedCompany,
      stageTimes: stageTimesQuery.data,
    });
  }, [preparedForecastOpenDeals, movementOpenDealsQuery.data, dealsQuery.data,
    expectedCloseV2CompanyForecast, pipelinesQuery.data, currenciesQuery.data,
    currentUser?.company, stageTimesQuery.data]);
  const evaluatedForecastOpenDeals = useMemo(() => evaluateOpenDealsForForecast(
    preparedV2ForecastOpenDeals, currentUser?.company_id, pipelinesQuery.data?.results ?? [],
  ), [preparedV2ForecastOpenDeals, currentUser?.company_id, pipelinesQuery.data]);
  const evaluateForecastForFilters = useCallback((requestedFilters: StateOfSalesV2FilterState) => {
    if (!currentUser?.company || isLoading || error) return null;
    const isForecastL2 = resolvedView === "forecast";
    const includeOpenDeals = resolvedView !== "forecast";

    const scopedPreparedDeals = filterDealsByGlobalSelection(
      preparedDeals,
      teams,
      requestedFilters,
    );
    const scopedMovementDeals = filterDealsByGlobalSelection(
      preparedForecastOpenDeals,
      teams,
      requestedFilters,
    );
    return evaluateRiskForecast(
      scopedPreparedDeals,
      currentUser.company_id,
      pipelinesQuery.data?.results ?? [],
      requestedFilters.period,
      "all",
      "all",
      getCompanyTarget(currentUser.company_id ?? 0),
      requestedFilters.accumulated,
      companyCurrency,
      {
        includeL2Details: isForecastL2,
        includeOpenDeals,
        movementDeals: scopedMovementDeals,
        dealstageAvgTimes: stageTimesQuery.data,
      },
    );
  }, [
    companyCurrency,
    currentUser?.company,
    currentUser?.company_id,
    error,
    isLoading,
    pipelinesQuery.data?.results,
    preparedDeals,
    preparedForecastOpenDeals,
    resolvedView,
    stageTimesQuery.data,
    teams,
  ]);
  const forecastLoadKey = `${currentUser?.company_id ?? "unknown"}:${resolvedView}`;
  const forecastLoadMeasurement = useRef({
    key: forecastLoadKey,
    startedAt: performanceNow(),
  });
  const measuredForecastLoads = useRef(new Set<string>());
  if (forecastLoadMeasurement.current.key !== forecastLoadKey) {
    forecastLoadMeasurement.current = {
      key: forecastLoadKey,
      startedAt: performanceNow(),
    };
  }
  const forecast = useMemo(() => {
    const startedAt = performanceNow();
    const result = evaluateForecastForFilters(filters);
    if (result) {
      console.info(
        `[State of Sales] ${resolvedView} calculation completed in ${(
          performanceNow() - startedAt
        ).toFixed(0)} ms`,
      );
    }
    return result;
  }, [evaluateForecastForFilters, filters, resolvedView]);

  useEffect(() => {
    if (
      !forecast
      || isLoading
      || error
      || typeof window === "undefined"
      || measuredForecastLoads.current.has(forecastLoadKey)
    ) return;

    const frame = window.requestAnimationFrame(() => {
      measuredForecastLoads.current.add(forecastLoadKey);
      console.info(
        `[State of Sales] ${resolvedView} ready for paint in ${(
          performanceNow() - forecastLoadMeasurement.current.startedAt
        ).toFixed(0)} ms`,
      );
    });
    return () => window.cancelAnimationFrame(frame);
  }, [error, forecast, forecastLoadKey, isLoading, resolvedView]);

  const hasLoadedPriorYearActual = priorYearWonQuery.data !== undefined
    && !priorYearWonQuery.error;

  const buildChartDataForFilters = useCallback((
    scopedForecast: NonNullable<typeof forecast>,
    requestedFilters: StateOfSalesV2FilterState,
  ) => {
    const scopedPriorYearWonDeals = filterDealsByGlobalSelection(
      preparedPriorYearWonDeals,
      teams,
      requestedFilters,
    );
    const scopedPriorYearActualByCurrentMonth = new Map<string, number>();
    for (const deal of scopedPriorYearWonDeals) {
      if (!deal.is_won || !deal.closed_at) continue;
      const shiftedDate = addMonths(new Date(deal.closed_at), 12);
      const key = `${shiftedDate.getFullYear()}-${String(shiftedDate.getMonth() + 1).padStart(2, "0")}`;
      scopedPriorYearActualByCurrentMonth.set(
        key,
        (scopedPriorYearActualByCurrentMonth.get(key) ?? 0) + Number(deal.value ?? 0),
      );
    }
    let accumulatedActual = 0;
    const selectedPeriodStart = scopedForecast.forecastChartSelectedPeriodStart;

    const canonicalRows = (scopedForecast.forecastChartDataWithContext ?? scopedForecast.forecastChartData ?? []).map((row) => {
      const actualLastYear = hasLoadedPriorYearActual
        ? scopedPriorYearActualByCurrentMonth.get(row.month) ?? 0
        : null;

      if (requestedFilters.accumulated && row.month === selectedPeriodStart) {
        accumulatedActual = 0;
      }

      if (requestedFilters.accumulated && actualLastYear !== null) {
        accumulatedActual += actualLastYear;
      }

      return {
        ...row,
        actual_last_year: requestedFilters.accumulated && actualLastYear !== null
          ? accumulatedActual
          : actualLastYear,
        is_context: selectedPeriodStart ? row.month < selectedPeriodStart : false,
      };
    });

    const companyForecast = expectedCloseV2Query.data;
    const scopedOpenDeals = filterDealsByGlobalSelection(
      evaluatedForecastOpenDeals,
      teams,
      requestedFilters,
    ).map(deal => ({
      adjustedCloseDate: deal.adjustedCloseDate,
      forecast_contribution: deal.forecast_contribution,
      id: Number(deal.id),
      value: Number(deal.value ?? 0),
    }));

    return applyExpectedCloseV2Forecast(
      canonicalRows,
      companyForecast,
      scopedOpenDeals,
      requestedFilters.accumulated,
      selectedPeriodStart,
    );
  }, [
    expectedCloseV2Query.data,
    hasLoadedPriorYearActual,
    evaluatedForecastOpenDeals,
    preparedPriorYearWonDeals,
    teams,
  ]);
  const chartData = useMemo(
    () => forecast ? buildChartDataForFilters(forecast, filters) : [],
    [buildChartDataForFilters, filters, forecast],
  );
  const getForecastDealsForMonths = (months: ReadonlySet<string>, requestedFilters = filters) => [...applyExpectedCloseV2DealContributions(
    filterDealsByGlobalSelection(evaluatedForecastOpenDeals, teams, requestedFilters),
    expectedCloseV2CompanyForecast, months,
  )];

  const pipelinesForSelect = (pipelinesQuery.data?.results ?? []).map(
    (item: { id: string; label: string }) => ({
      value: item.id,
      label: item.label,
    }),
  );
  const teamsForSelect = teams.map((team) => ({
    value: String(team.id),
    label: team.name,
  }));
  const ownersForSelect = useMemo(() => {
    const unique = new Map<string, string>();
    for (const deal of preparedDeals) {
      const dealOwner = getDealOwner(deal);
      if (!dealOwner?.id) continue;
      unique.set(dealOwner.id, `${dealOwner.first_name} ${dealOwner.last_name}`.trim());
    }
    return Array.from(unique, ([value, label]) => ({ value, label }));
  }, [preparedDeals]);

  const retry = () => Promise.all([
    expectedCloseV2Query.mutate(),
    dealsQuery.mutate(),
    movementOpenDealsQuery.mutate(),
    priorYearWonQuery.mutate(),
    currenciesQuery.mutate(),
    teamsQuery.mutate(),
    pipelinesQuery.mutate(),
    stageTimesQuery.mutate(),
  ]);

  return {
    accumulated,
    chartData,
    getForecastDealsForMonths,
    filteredEvaluatedForecastOpenDeals: filterDealsByGlobalSelection(evaluatedForecastOpenDeals, teams, filters),
    companyCurrency,
    buildChartDataForFilters,
    error,
    filters,
    filteredPreparedDeals,
    filteredPreparedMovementOpenDeals,
    forecast,
    evaluateForecastForFilters,
    isLoading,
    optionalError: sourceState.optionalError,
    optionalErrorSources: sourceState.optionalErrorSources,
    optionalLoading: sourceState.optionalLoading,
    ownersForSelect,
    period,
    pipelinesForSelect,
    expectedCloseV2Available: Boolean(expectedCloseV2CompanyForecast),
    expectedCloseV2CompanyForecast,
    forecastEnrichmentLoading: Boolean(
      usesExpectedCloseV2
      && movementOpenDealsQuery.isLoading
    ),
    selectedPeriodStart: forecast?.forecastChartSelectedPeriodStart ?? null,
    retry,
    selectedOwners,
    selectedPipelines,
    selectedTeams,
    sourceErrorSources: sourceState.requiredErrorSources,
    setAccumulated,
    setPeriod,
    setSelectedOwners,
    setSelectedPipelines,
    setSelectedTeams,
    teams,
    teamsForSelect,
    usesExpectedCloseV2,
  };
}
